import React, { useEffect, useState } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { api, type PushSettings } from '../api';
import { Switch, Field, Input, Button } from '../components/ui';
import { askForPush, isPushAvailable, pushPermission } from '../push';
import { isAndroidApp } from '../androidApp';

type Kind = 'chat' | 'album' | 'hunt' | 'invite' | 'purchase';

const KINDS: { k: Kind; label: string; sub: string }[] = [
  { k: 'chat', label: 'Chatt', sub: 'Gruppchatten och jaktchatten' },
  { k: 'album', label: 'Bilder', sub: 'Nya bilder i albumet och från åtelkameror' },
  { k: 'hunt', label: 'Jakter', sub: 'När någon startar en jakt på din mark' },
  { k: 'invite', label: 'Inbjudningar', sub: 'Inbjudan, ändrad eller inställd jakt, svar till jaktledaren' },
  { k: 'purchase', label: 'Köp', sub: 'Köpta jakter att godkänna, och besked på dina köp' },
];

const DEFAULT_QUIET = { quietStart: '22:00', quietEnd: '07:00' };

/** Profil → Notiser (N1): av/på per typ och tysta timmar. Notiserna kommer till iPhone- och Android-appen. */
export function PushSettingsSection({ cardStyle }: { cardStyle: React.CSSProperties }) {
  const T = getTheme(useThemeMode().mode);
  const [s, setS] = useState<PushSettings | null>(null);
  const [perm, setPerm] = useState<'granted' | 'denied' | 'prompt' | 'unavailable'>('unavailable');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.pushSettings().then(setS).catch(e => setError(e.message || 'Kunde inte läsa inställningarna'));
    void pushPermission().then(setPerm);
  }, []);

  async function save(change: Partial<Omit<PushSettings, 'devices'>>) {
    if (!s) return;
    const prev = s;
    setS({ ...s, ...change });
    setError(''); setSaved(false);
    try {
      setS(await api.updatePushSettings(change));
      setSaved(true);
    } catch (e: any) {
      setS(prev);
      setError(e.message || 'Kunde inte spara');
    }
  }

  async function turnOn() {
    const ok = await askForPush();
    setPerm(await pushPermission());
    if (ok) setTimeout(() => api.pushSettings().then(setS).catch(() => {}), 1500);
  }

  const quiet = !!(s?.quietStart && s?.quietEnd);
  const sub = { color: T.textSub, fontSize: 13, lineHeight: 1.5 } as const;

  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={{ color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Notiser</div>

      {!isPushAvailable() && <div style={{ ...sub, marginBottom: 10 }}>
        Notiserna kommer till Qvenari-appen på telefonen (och Apple Watch).{s ? ` ${s.devices === 0 ? 'Ingen telefon har slagit på notiser ännu.' : s.devices === 1 ? 'En telefon tar emot dem.' : `${s.devices} telefoner tar emot dem.`}` : ''}
      </div>}
      {perm === 'prompt' && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ ...sub, flex: 1, minWidth: 180 }}>Få en notis när något händer på dina marker.</div>
        <Button size="sm" onClick={turnOn}>Slå på notiser</Button>
      </div>}
      {perm === 'denied' && <div style={{ ...sub, marginBottom: 10 }}>
        Notiser är avstängda för Qvenari på den här telefonen. {isAndroidApp()
          ? 'Slå på dem i telefonens Inställningar → Appar → venari → Notiser.'
          : 'Slå på dem i iPhones Inställningar → Qvenari → Notiser.'}
      </div>}
      {perm === 'granted' && <div style={{ ...sub, marginBottom: 10 }}>Notiser är på för den här telefonen.</div>}

      {!s && !error && <div style={{ color: T.textMuted, fontSize: 13 }}>Laddar…</div>}
      {s && <>
        {KINDS.map(({ k, label, sub: hint }) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderTop: `1px solid ${T.border}`, padding: '4px 0' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{label}</div>
              <div style={{ color: T.textMuted, fontSize: 12 }}>{hint}</div>
            </div>
            <Switch checked={s[k]} onChange={v => save({ [k]: v })} ariaLabel={`Notiser: ${label}`} />
          </div>
        ))}
        <div style={{ borderTop: `1px solid ${T.border}`, padding: '4px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>Tysta timmar</div>
              <div style={{ color: T.textMuted, fontSize: 12 }}>Notiserna kommer ändå, men utan ljud och utan att skärmen tänds</div>
            </div>
            <Switch checked={quiet} onChange={v => save(v ? DEFAULT_QUIET : { quietStart: '', quietEnd: '' })} ariaLabel="Tysta timmar" />
          </div>
          {quiet && <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
            <Field label="Från" style={{ flex: 1 }}>
              <Input type="time" value={s.quietStart ?? ''} onChange={e => e.target.value && save({ quietStart: e.target.value })} />
            </Field>
            <Field label="Till" style={{ flex: 1 }}>
              <Input type="time" value={s.quietEnd ?? ''} onChange={e => e.target.value && save({ quietEnd: e.target.value })} />
            </Field>
          </div>}
        </div>
      </>}
      {error && <div style={{ color: T.danger, fontSize: 13, marginTop: 8 }}>{error}</div>}
      {saved && !error && <div role="status" style={{ color: T.textMuted, fontSize: 12, marginTop: 6 }}>Sparat</div>}
    </div>
  );
}
